// src/pages/admin/herosection/HeroSectionManagement.jsx
import React, { useState, useEffect, useContext } from "react";
import { Form, Spinner } from "react-bootstrap";
import {
  FiEdit2,
  FiTrash2,
  FiSave,
  FiX,
  FiSearch,
  FiFilter,
  FiUpload,
  FiLayout,
  FiCheckCircle,
  FiMinusCircle,
  FiXCircle,
} from "react-icons/fi";
import AdminMasterLayout from "../../../layouts/admin/AdminMasterLayout";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import axios from "axios";
import "../../../assets/css/admin/screens/herosectionmgmt/AdminHerosectionmanagement.css";
import AppContext from "../../../contexts/AppContext";

const SEP = ",";

const HeroSectionManagement = () => {
  const { baseUrl, baseUrlForFiles, adminAuthToken } = useContext(AppContext);
  const [heroSections, setHeroSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreateUpdateLoading, setIsCreateUpdateLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [showModal, setShowModal] = useState(false);
  const [editingHero, setEditingHero] = useState(null);
  const [heroId, setHeroId] = useState(null);
  const [heading, setHeading] = useState("");
  const [subtext, setSubtext] = useState("");
  const [mainImageFile, setMainImageFile] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState("");
  const [mainImageExisting, setMainImageExisting] = useState("");
  const [mainFileName, setMainFileName] = useState("");
  const [bubbleFiles, setBubbleFiles] = useState([]);
  const [bubblePreviews, setBubblePreviews] = useState([]);
  const [bubbleExisting, setBubbleExisting] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [stats, setStats] = useState(null);

  const headingParts = heading.split(SEP).map((s) => s.trim());
  const line1Val = headingParts[0] || "";
  const line2Val = headingParts[1] || "";
  const line3Val = headingParts[2] || "";

  const resetForm = () => {
    setEditingHero(null);
    setHeroId(null);
    setIsActive(true);
    setHeading("");
    setSubtext("");
    setMainImageFile(null);
    setMainImagePreview("");
    setMainImageExisting("");
    setMainFileName("");
    setBubbleFiles([]);
    setBubblePreviews([]);
    setBubbleExisting([]);
  };

  const viewAllHeroSection = () => {
    setLoading(true);

    const loggedInAdmin = JSON.parse(localStorage.getItem("loggedInAdmin"));
    const adminId = loggedInAdmin?.id;

    axios
      .post(
        `${baseUrl}/admin/herosection/viewHeroSection`,
        {
          chadmin_id: adminId,
        },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${adminAuthToken}`,
          },
        },
      )
      .then((res) => {
        setHeroSections(res.data.hero || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const viewHeroStats = () => {
    axios
      .get(`${baseUrl}/admin/herosection/stats`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminAuthToken}`,
        },
      })
      .then((res) => setStats(res.data))
      .catch(() => {});
  };

  const createHeroSection = () => {
    if (heading.split(SEP).length > 3) {
      toast.error("Maximum 3 lines allowed");
      return;
    }
    setIsCreateUpdateLoading(true);
    const loggedInAdmin = JSON.parse(localStorage.getItem("loggedInAdmin"));
    const adminId = loggedInAdmin?.id;
    const formdata = new FormData();
    formdata.append("chadmin_id", adminId);
    formdata.append("line1", line1Val);
    formdata.append("line2", line2Val);
    formdata.append("line3", line3Val);
    formdata.append("subtext", subtext);
    formdata.append("is_active", isActive ? 1 : 0);
    if (mainImageFile) formdata.append("mainImage", mainImageFile);
    bubbleFiles.forEach((file) => formdata.append("bubbleImages[]", file));
    axios
      .post(`${baseUrl}/admin/herosection/createHeroSection`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${adminAuthToken}`,
        },
      })
      .then(() => {
        viewAllHeroSection();
        viewHeroStats();
        resetForm();
        setShowModal(false);
        setIsCreateUpdateLoading(false);
        toast.success("Hero Section created successfully!");
      })
      .catch(() => {
        setIsCreateUpdateLoading(false);
        toast.error("Failed to create hero section.");
      });
  };

  const updateHeroSection = async () => {
    setShowModal(false);

    const result = await Swal.fire({
      title: "Save Changes?",
      text: "Do you want to update this hero section?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#FF8559",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Update!",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    setIsCreateUpdateLoading(true);
    const loggedInAdmin = JSON.parse(localStorage.getItem("loggedInAdmin"));
    const adminId = loggedInAdmin?.id;
    const formdata = new FormData();
    formdata.append("chadmin_id", adminId);
    formdata.append("id", heroId);
    formdata.append("line1", line1Val);
    formdata.append("line2", line2Val);
    formdata.append("line3", line3Val);
    formdata.append("subtext", subtext);
    formdata.append("is_active", isActive ? 1 : 0);
    if (mainImageFile) formdata.append("mainImage", mainImageFile);
    bubbleFiles.forEach((file) => formdata.append("bubbleImages[]", file));

    axios
      .post(`${baseUrl}/admin/herosection/updateHeroSection`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${adminAuthToken}`,
        },
      })
      .then((res) => {
        setIsCreateUpdateLoading(false);
        if (res.data.success) {
          viewAllHeroSection();
          viewHeroStats();
          resetForm();
          toast.success("Hero Section updated successfully!");
        } else {
          toast.error("Update failed. Hero section not found.");
        }
      })
      .catch(() => {
        setIsCreateUpdateLoading(false);
        toast.error("Update failed. Please try again.");
      });
  };

  const toggleStatus = (id) => {
    const formdata = new FormData();
    formdata.append("id", id);
    axios
      .post(`${baseUrl}/admin/herosection/toggleStatus`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${adminAuthToken}`,
        },
      })
      .then(() => {
        toast.success("Status Updated");
        viewAllHeroSection();
        viewHeroStats();
      })
      .catch(() => toast.error("Failed to update status"));
  };

  const deleteHeroSection = (id) => {
    const formdata = new FormData();
    formdata.append("herosectionId", id);
    axios
      .post(`${baseUrl}/admin/herosection/deleteherosection`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${adminAuthToken}`,
        },
      })
      .then(() => {
        toast.error("HeroSection Deleted");
        viewAllHeroSection();
        viewHeroStats();
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    viewAllHeroSection();
    viewHeroStats();
  }, []);

  const filteredHeroSections = heroSections.filter((h) => {
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      h.line1?.toLowerCase().includes(q) ||
      h.line2?.toLowerCase().includes(q) ||
      h.subtext?.toLowerCase().includes(q);
    const matchesStatus =
      filterStatus === "All" ||
      (filterStatus === "Active" && h.is_active) ||
      (filterStatus === "Inactive" && !h.is_active);
    return matchesSearch && matchesStatus;
  });

  const getStatusClass = (isActive) =>
    isActive ? "hro-status--active" : "hro-status--inactive";

  return (
    <AdminMasterLayout>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="hro-card-shell">
        {/* ── Header ── */}
        <div className="hro-header-section">
          <div>
            <h3 className="hro-section-title">Hero Section Management</h3>
            <p className="hro-section-subtitle">
              Manage the homepage hero section content
            </p>
          </div>
          <button
            className="hro-create-btn"
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            <span>+</span> Add Hero Section
          </button>
        </div>

        {/* ── Stat Cards ── */}
        <div className="hro-summary-grid">
          <div className="hro-summary-card">
            <div>
              <span className="hro-label">Total Sections</span>
              <h3>{stats?.total_sections ?? "0"}</h3>
              <p className="hro-muted mb-0">All hero sections</p>
            </div>
            <div className="hro-card-icon">
              <FiLayout size={22} />
            </div>
          </div>
          <div className="hro-summary-card">
            <div>
              <span className="hro-label">Active</span>
              <h3>{stats?.active_sections ?? "0"}</h3>
              <p className="hro-muted mb-0">Currently visible</p>
            </div>
            <div className="hro-card-icon hro-card-icon--green">
              <FiCheckCircle size={22} />
            </div>
          </div>
          <div className="hro-summary-card">
            <div>
              <span className="hro-label">Inactive</span>
              <h3>{stats?.inactive_sections ?? "0"}</h3>
              <p className="hro-muted mb-0">Not showing</p>
            </div>
            <div className="hro-card-icon hro-card-icon--red">
              <FiMinusCircle size={22} />
            </div>
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div className="hro-toolbar">
          <div className="hro-search-group">
            <span className="hro-search-icon">
              <FiSearch size={14} />
            </span>
            <input
              className="hro-search-input"
              placeholder="Search by heading or sub-text..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="hro-filter-group">
            <FiFilter size={14} />
            <select
              className="hro-filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="All">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="hro-layout">
          <div className="hro-table-wrapper">
            <table className="hro-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Heading</th>
                  <th>Sub-text</th>
                  <th>Main Image</th>
                  <th>Bubbles</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={7} className="text-center py-4">
                      <Spinner
                        animation="border"
                        size="sm"
                        className="hro-spinner"
                      />
                      <span className="ms-2 hro-muted hro-loading-text">
                        Loading...
                      </span>
                    </td>
                  </tr>
                )}

                {!loading && filteredHeroSections.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-5">
                      <div className="hro-empty-icon">🖼️</div>
                      <p className="hro-muted mt-2">
                        {heroSections.length === 0
                          ? "No hero sections found."
                          : "No sections match the selected filters."}
                      </p>
                    </td>
                  </tr>
                )}

                {!loading &&
                  filteredHeroSections.map((h, i) => (
                    <tr key={h.id}>
                      <td>
                        <span className="hro-id">#{i + 1}</span>
                      </td>
                      <td>
                        <div className="hro-heading-cell">
                          <span>{h.line1}</span>
                          <span className="hro-line2">{h.line2}</span>
                          <span className="hro-line3">{h.line3}</span>
                        </div>
                      </td>
                      <td>
                        <p className="hro-subtext">{h.subtext}</p>
                      </td>
                      <td>
                        {h.mainImage ? (
                          <img
                            src={`${baseUrlForFiles}/${h.mainImage}`}
                            alt="hero"
                            className="hro-table-thumb"
                          />
                        ) : (
                          <span className="hro-muted">—</span>
                        )}
                      </td>
                      <td>
                        <div className="hro-bubbles-preview">
                          {Array.isArray(h.bubble_images) &&
                          h.bubble_images.length > 0 ? (
                            h.bubble_images.map((img, idx) => (
                              <img
                                key={idx}
                                src={`${baseUrlForFiles}/${img.image_path}`}
                                alt={`bubble ${idx + 1}`}
                                className="hro-bubble-mini"
                              />
                            ))
                          ) : (
                            <span className="hro-muted">—</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`hro-status ${getStatusClass(h.is_active)}`}
                        >
                          {h.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <div className="hro-actions">
                          <button
                            className="hro-action-btn hro-action-btn--edit"
                            title="Edit"
                            onClick={() => {
                              setEditingHero(h);
                              setHeroId(h.id);
                              setHeading(
                                `${h.line1 || ""} ${SEP} ${h.line2 || ""} ${SEP} ${h.line3 || ""}`,
                              );
                              setSubtext(h.subtext || "");
                              setMainImageFile(null);
                              setMainImagePreview("");
                              setMainImageExisting(h.mainImage || "");
                              setMainFileName("");
                              setBubbleFiles([]);
                              setBubblePreviews([]);
                              setBubbleExisting(h.bubble_images || []);
                              setIsActive(
                                h.is_active === 1 || h.is_active === true,
                              );
                              setShowModal(true);
                            }}
                          >
                            <FiEdit2 size={13} />
                          </button>
                          <button
                            className={`hro-action-btn ${h.is_active ? "hro-action-btn--deactivate" : "hro-action-btn--activate"}`}
                            title={h.is_active ? "Deactivate" : "Activate"}
                            onClick={() => toggleStatus(h.id)}
                          >
                            <FiXCircle size={13} />
                          </button>
                          <button
                            className="hro-action-btn hro-action-btn--delete"
                            title="Delete"
                            onClick={() => deleteHeroSection(h.id)}
                          >
                            <FiTrash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div
          className="hro-modal-overlay"
          onClick={() => {
            setShowModal(false);
            resetForm();
          }}
        >
          <div
            className="hro-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="hro-modal-header">
              <h4>
                {editingHero ? "Edit Hero Section" : "Create Hero Section"}
              </h4>
              <button
                className="hro-modal-close"
                onClick={() => {
                  resetForm();
                  setShowModal(false);
                }}
              >
                <FiX size={20} />
              </button>
            </div>

            <Form className="hro-modal-form">
              <div className="hro-form-grid">
                {/* ── LEFT ── */}
                <div className="hro-form-left">
                  <Form.Group className="hro-form-group">
                    <Form.Label>
                      Heading{" "}
                      <span className="hro-sep-hint">
                        (Use , to separate lines)
                      </span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Line1 , Line2 , Line3"
                      value={heading}
                      onChange={(e) => setHeading(e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="hro-form-group">
                    <Form.Label>Sub-text</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Sub-text"
                      value={subtext}
                      onChange={(e) => setSubtext(e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="hro-form-group">
                    <label className="hro-checkbox-row">
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                      />
                      <span className="hro-checkbox-label">Is Active</span>
                    </label>
                  </Form.Group>
                </div>

                {/* ── RIGHT ── */}
                <div className="hro-form-right">
                  <Form.Group className="hro-form-group">
                    <Form.Label>Main Hero Image</Form.Label>
                    {(mainImagePreview || mainImageExisting) && (
                      <div className="hro-main-preview-wrap">
                        <img
                          src={
                            mainImagePreview ||
                            `${baseUrlForFiles}/${mainImageExisting}`
                          }
                          alt="preview"
                          className="hro-img-preview"
                        />
                        {mainImagePreview && (
                          <button
                            type="button"
                            className="hro-remove-btn"
                            onClick={() => {
                              setMainImageFile(null);
                              setMainImagePreview("");
                              setMainFileName("");
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    )}
                    <div className="hro-file-input-wrapper">
                      <label htmlFor="main-image" className="hro-file-label">
                        <FiUpload size={15} />
                        <span>{mainFileName || "Choose File"}</span>
                      </label>
                      <input
                        type="file"
                        id="main-image"
                        accept="image/*"
                        className="hro-file-input"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setMainImageFile(file);
                            setMainImagePreview(URL.createObjectURL(file));
                            setMainImageExisting("");
                            setMainFileName(file.name);
                          }
                        }}
                      />
                    </div>
                  </Form.Group>

                  <Form.Group className="hro-form-group">
                    <Form.Label>Floating Bubble Images (max 5)</Form.Label>
                    {bubbleExisting.length > 0 &&
                      bubblePreviews.length === 0 && (
                        <div className="hro-bubble-strip">
                          {bubbleExisting.map((b, i) => (
                            <div key={i} className="hro-bubble-chip">
                              <img
                                src={`${baseUrlForFiles}/${b.image_path}`}
                                alt=""
                              />
                              <span className="hro-chip-num">{i + 1}</span>
                            </div>
                          ))}
                          <p className="hro-existing-note">
                            Upload new to replace
                          </p>
                        </div>
                      )}
                    {bubblePreviews.length > 0 && (
                      <div className="hro-bubble-strip">
                        {bubblePreviews.map((src, i) => (
                          <div key={i} className="hro-bubble-chip">
                            <img src={src} alt="" />
                            <span className="hro-chip-num">{i + 1}</span>
                            <button
                              type="button"
                              className="hro-chip-remove"
                              onClick={() => {
                                const newP = bubblePreviews.filter(
                                  (_, idx) => idx !== i,
                                );
                                const newF = bubbleFiles.filter(
                                  (_, idx) => idx !== i,
                                );
                                setBubblePreviews(newP);
                                setBubbleFiles(newF);
                                if (newP.length === 0)
                                  setBubbleExisting(bubbleExisting);
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="hro-file-input-wrapper">
                      <label htmlFor="bubble-images" className="hro-file-label">
                        <FiUpload size={15} />
                        <span>
                          {bubbleFiles.length > 0
                            ? `${bubbleFiles.length} file(s) selected`
                            : "Choose Bubble Images"}
                        </span>
                      </label>
                      <input
                        type="file"
                        id="bubble-images"
                        accept="image/*"
                        multiple
                        className="hro-file-input"
                        onChange={(e) => {
                          const files = Array.from(e.target.files).slice(0, 5);
                          setBubbleFiles(files);
                          setBubblePreviews(
                            files.map((f) => URL.createObjectURL(f)),
                          );
                          setBubbleExisting([]);
                        }}
                      />
                    </div>
                  </Form.Group>
                </div>
              </div>

              {/* ── Footer ── */}
              <div className="hro-modal-footer">
                <button
                  type="button"
                  className="hro-btn-cancel"
                  disabled={isCreateUpdateLoading}
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="hro-btn-save"
                  disabled={isCreateUpdateLoading}
                  onClick={editingHero ? updateHeroSection : createHeroSection}
                >
                  {isCreateUpdateLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      {editingHero ? "Updating..." : "Creating..."}
                    </>
                  ) : (
                    <>
                      <FiSave size={14} className="me-1" />
                      {editingHero
                        ? "Update Hero Section"
                        : "Create Hero Section"}
                    </>
                  )}
                </button>
              </div>
            </Form>
          </div>
        </div>
      )}
    </AdminMasterLayout>
  );
};

export default HeroSectionManagement;